import React from 'react';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Box,
  Tooltip
} from '@mui/material';
import {
  CalendarMonth,
  CalendarToday,
  CalendarViewMonth,
  ChevronLeft,
  ChevronRight,
  Search,
  Today as TodayIcon,
  ViewWeek,
  ZoomIn,
  ZoomOut,
  AccessTime
} from '@mui/icons-material';
import dayjs from 'dayjs';

export default function Toolbar({
  viewMode,
  onChangeView,
  goPrev,
  goNext,
  goToday,
  zoomIn,
  zoomOut,
  rangeStart,
  rangeEnd,
  filterReservationStatus = 'all',
  onChangeReservationStatus,
  searchGuest = '',
  onChangeSearch,
  statusCounts = {}, // { all, confirmed, checked_in, tentative, group, walk_in, cancelled, no_show }
  pmsColors
}) {
  const handleViewMode = (event, newMode) => {
    if (newMode !== null) onChangeView(newMode);
  };

  // Pills a mostrar — preserva el orden de importancia operativa
  const pills = [
    { key: 'all', label: 'Todas', color: pmsColors.text.primary },
    { key: 'confirmed', label: 'Confirmadas', color: pmsColors.reservations.confirmed },
    { key: 'checked_in', label: 'In-House', color: pmsColors.reservations.checked_in },
    { key: 'tentative', label: 'Tentativas', color: pmsColors.reservations.tentative },
    { key: 'group', label: 'Grupos', color: pmsColors.reservations.group },
    { key: 'walk_in', label: 'Walk-in', color: pmsColors.reservations.walk_in }
  ];

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%', overflowX: 'auto', py: 0.5 }}>
      {/* Navegación temporal */}
      <Stack
        direction="row"
        spacing={0.25}
        alignItems="center"
        sx={{
          backgroundColor: pmsColors.ui.background,
          p: 0.25,
          borderRadius: 1.25,
          border: `1px solid ${pmsColors.ui.border}`
        }}
      >
        <IconButton size="small" onClick={goPrev}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Button
          size="small"
          startIcon={<TodayIcon sx={{ fontSize: 14 }} />}
          onClick={goToday}
          sx={{
            color: pmsColors.text.primary,
            fontWeight: 700,
            minWidth: 'auto',
            px: 1.5,
            textTransform: 'none',
            backgroundColor: pmsColors.ui.surface,
            border: `1px solid ${pmsColors.ui.border}`,
            '&:hover': { backgroundColor: pmsColors.ui.hover }
          }}
        >
          Hoy
        </Button>
        <IconButton size="small" onClick={goNext}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Stack>

      {/* Chip con rango de fechas visible */}
      {rangeStart && rangeEnd && (
        <Tooltip title="Rango visible">
          <Chip
            icon={<CalendarViewMonth sx={{ fontSize: 14 }} />}
            label={`${dayjs(rangeStart).format('DD MMM')} – ${dayjs(rangeEnd).format('DD MMM')}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, borderColor: pmsColors.ui.border, bgcolor: pmsColors.ui.surface }}
          />
        </Tooltip>
      )}

      <Divider orientation="vertical" flexItem sx={{ height: 22, alignSelf: 'center', mx: 0.5 }} />

      {/* Selector de vista */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewMode}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            border: 0,
            borderRadius: 1,
            mx: 0.25,
            px: 1.25,
            py: 0.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '0.75rem',
            color: pmsColors.text.secondary,
            '&.Mui-selected': {
              backgroundColor: pmsColors.secondary.main + '14',
              color: pmsColors.secondary.main,
              fontWeight: 800
            },
            '&:hover': { backgroundColor: pmsColors.ui.hover }
          }
        }}
      >
        <ToggleButton value="hourly">
          <AccessTime sx={{ mr: 0.5, fontSize: 14 }} /> Horas
        </ToggleButton>
        <ToggleButton value="day">
          <CalendarToday sx={{ mr: 0.5, fontSize: 14 }} /> 3 Días
        </ToggleButton>
        <ToggleButton value="week">
          <ViewWeek sx={{ mr: 0.5, fontSize: 14 }} /> Semana
        </ToggleButton>
        <ToggleButton value="twoWeeks">
          <CalendarViewMonth sx={{ mr: 0.5, fontSize: 14 }} /> 2 Sem
        </ToggleButton>
        <ToggleButton value="month">
          <CalendarMonth sx={{ mr: 0.5, fontSize: 14 }} /> Mes
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem sx={{ height: 22, alignSelf: 'center', mx: 0.5 }} />

      {/* Filter pills con contadores */}
      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'nowrap' }}>
        {pills.map((p) => {
          const count = statusCounts[p.key] ?? 0;
          const active = filterReservationStatus === p.key;
          return (
            <Chip
              key={p.key}
              size="small"
              clickable
              onClick={() => onChangeReservationStatus && onChangeReservationStatus(p.key)}
              label={
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: p.color,
                      display: 'inline-block'
                    }}
                  />
                  <span>{p.label}</span>
                  <Box
                    component="span"
                    sx={{
                      ml: 0.25,
                      px: 0.6,
                      py: '1px',
                      borderRadius: 0.75,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      backgroundColor: active ? 'rgba(255,255,255,.25)' : pmsColors.ui.background,
                      color: active ? '#fff' : pmsColors.text.primary
                    }}
                  >
                    {count}
                  </Box>
                </Box>
              }
              sx={{
                height: 26,
                px: 0.5,
                fontWeight: 600,
                fontSize: '0.72rem',
                borderRadius: 999,
                border: `1px solid ${active ? 'transparent' : pmsColors.ui.border}`,
                backgroundColor: active ? pmsColors.secondary.main : pmsColors.ui.surface,
                color: active ? '#fff' : pmsColors.text.primary,
                '&:hover': {
                  backgroundColor: active ? pmsColors.secondary.dark : pmsColors.ui.hover
                }
              }}
            />
          );
        })}
      </Stack>

      <Box sx={{ flex: 1 }} />

      {/* Zoom */}
      <Stack
        direction="row"
        spacing={0}
        sx={{ border: `1px solid ${pmsColors.ui.border}`, borderRadius: 1, bgcolor: pmsColors.ui.surface }}
      >
        <IconButton size="small" onClick={zoomOut} sx={{ borderRadius: 0 }}>
          <ZoomOut fontSize="small" />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton size="small" onClick={zoomIn} sx={{ borderRadius: 0 }}>
          <ZoomIn fontSize="small" />
        </IconButton>
      </Stack>

      {/* Búsqueda */}
      <TextField
        size="small"
        placeholder="Buscar huésped, reserva…"
        value={searchGuest}
        onChange={(e) => onChangeSearch && onChangeSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" sx={{ color: pmsColors.text.secondary }} />
            </InputAdornment>
          ),
          sx: {
            fontSize: '0.82rem',
            height: 32,
            backgroundColor: pmsColors.ui.background,
            borderRadius: 1,
            '& fieldset': { border: 'none' }
          }
        }}
        sx={{ width: 200 }}
      />
    </Stack>
  );
}
